import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { FloorAssemblyOverride, GeneratedFloorBoundaries } from "building-types/floors/floorAssemblyTypes";
import type { FloorSystemExternal } from "building-types/floors/floorSystemTypes";
import type { Foundation, FoundationSystem } from "building-types/foundations/foundationSystemTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { PlanarGraphSegmentEntry } from "building-types/planarGraphs/planarGraphSegmentTypes";
import type { RWTable, Table } from "common/data/table";
import { evalFormulas } from "doc/formula/evalFormulas";
import type { Segment } from "geom/polyclip/dcel";
import { polyLine2Edge, polyLine2EdgeCount } from "geom/polyLine2";
import { getEdgeIdsForPerimeter } from "../elements/floor/floor";
import { floorPerimeterInfoToUse } from "../elements/floor/floorMirrorUtils";
import { foundationPerimeterInfoToUse } from "../elements/foundation/foundationMirrorUtils";
import { shouldProcessFloorWithBoundaries } from "./generateFloorHorizontalAssembliesTools";

export function getHorizontalAssemblyPerimeterInfo(input: {
  floorSystems: Table<FloorSystemExternal>;
  floors: Table<FloorAssemblyOverride>;
  floorBoundaries: Table<GeneratedFloorBoundaries>;
  foundationSystems: Table<FoundationSystem>;
  foundations: Table<Foundation>;
  datumAbstractions: Table<DatumAbstraction>;
  optionChoices: OptionChoices;
  entityDisabledByGroup: Table<boolean>;
  mirror: boolean;
  mirrorTranslationX: number;
}): {
  segmentsTable: Table<PlanarGraphSegmentEntry[]>;
} {
  const {
    floorSystems,
    floors,
    floorBoundaries,
    foundationSystems,
    foundations,
    optionChoices,
    entityDisabledByGroup,
    mirror,
    mirrorTranslationX,
  } = input;

  const segmentsTable: RWTable<PlanarGraphSegmentEntry[]> = {};

  const resolvedFoundationSystems = evalFormulas(
    foundationSystems,
    (v) => optionChoices[v],
    undefined,
    entityDisabledByGroup,
  );
  const resolvedFoundations = evalFormulas(foundations, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const foundationId in resolvedFoundations) {
    const foundation = resolvedFoundations[foundationId];
    if (!foundation || foundation.disabled) {
      continue;
    }
    const foundationSystemId = foundation.parentId;
    if (resolvedFoundationSystems[foundationSystemId]?.disabled) {
      continue;
    }

    const { perimeter, edgeIds } = foundation;

    if (!perimeter || !edgeIds) {
      continue;
    }

    const { perimeter: perimeterToUse } = foundationPerimeterInfoToUse(
      perimeter,
      edgeIds,
      foundation.foundationEdgeOverrides,
      mirror,
      mirrorTranslationX,
      {
        datumAbstractions: input.datumAbstractions,
        foundationLateralDatumId: foundation.lateralDatumId,
      },
    );
    const currentPolygonIndex = 0;
    //We only have one polygon here the way it is constructed per foundation
    const freeSegments: Segment[] = [];
    const segmentPolygonIds: number[] = [];
    const edgeCount = polyLine2EdgeCount(perimeterToUse);
    for (let i = 0, l = edgeCount; i < l; i++) {
      const plEdge = polyLine2Edge(perimeterToUse, i);
      freeSegments.push([plEdge[0], plEdge[1], plEdge[2], plEdge[3]]);
      segmentPolygonIds.push(currentPolygonIndex);
    }

    segmentsTable[foundationId] = freeSegments.map((segment) => ({ segment, polygonIndex: 0 }));
  }

  const resolvedFloorSystems = evalFormulas(floorSystems, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  const resolvedFloors = evalFormulas(floors, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const floorId in resolvedFloors) {
    const floor = resolvedFloors[floorId];
    const floorSystemId = floor.parentId;

    if (!shouldProcessFloorWithBoundaries(floor, floorSystemId, resolvedFloorSystems, floorBoundaries)) {
      continue;
    }
    const boundaries = floorBoundaries[floorId];
    if (!boundaries || boundaries.perimeters.length === 0) {
      continue;
    }

    const allSegments: PlanarGraphSegmentEntry[] = [];
    const floorEdgeOverrides = boundaries.floorEdgeOverrides;
    const combinedEdgeIds = boundaries.edgeIds;
    const perimeterEdgeCounts = boundaries.perimeterEdgeCounts;

    for (let perimeterIndex = 0; perimeterIndex < boundaries.perimeters.length; perimeterIndex++) {
      const perimeter = boundaries.perimeters[perimeterIndex];
      const edgeIdsForPerimeter = getEdgeIdsForPerimeter(combinedEdgeIds, perimeterEdgeCounts, perimeterIndex);
      const { perimeter: perimeterToUse } = floorPerimeterInfoToUse(
        perimeter,
        edgeIdsForPerimeter,
        floorEdgeOverrides,
        floor.lateralOffset,
      );
      const edgeCount = polyLine2EdgeCount(perimeterToUse);
      for (let i = 0; i < edgeCount; i++) {
        const plEdge = polyLine2Edge(perimeterToUse, i);
        allSegments.push({
          segment: [plEdge[0], plEdge[1], plEdge[2], plEdge[3]],
          polygonIndex: perimeterIndex,
        });
      }
    }

    segmentsTable[floorId] = allSegments;
  }

  return { segmentsTable };
}
